import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaPhoneAlt,
} from "react-icons/fa";
import Lottie from "lottie-react";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import loginAnimation from "../../assets/animations/login-animation.json";
import API from "../../services/api";
import "../../styles/auth.css";

const Login = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginType, setLoginType] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const rememberedIdentifier = localStorage.getItem("rememberedIdentifier");
    const rememberedPassword = localStorage.getItem("rememberedPassword");

    if (rememberedIdentifier && rememberedPassword) {
      setIdentifier(rememberedIdentifier);
      setPassword(rememberedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate fields are not empty
    if (!identifier.trim()) {
      toast.error(
        `Please enter your ${loginType === "email" ? "email address" : "phone number"}`,
      );
      return;
    }

    if (!password.trim()) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);

    try {
      const response = await login(identifier, password);

      // Show success message
      toast.success(response.message || "Login successful! Redirecting...", {
        autoClose: 2000, // Show for 2 seconds
      });

      // Small delay to allow toast to be seen
      setTimeout(() => {
        if (rememberMe) {
          localStorage.setItem("rememberedIdentifier", identifier);
          localStorage.setItem("rememberedPassword", password);
        } else {
          localStorage.removeItem("rememberedIdentifier");
          localStorage.removeItem("rememberedPassword");
        }

        const user = response.data?.user || response.user;
        if (user?.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/student");
        }
      }, 500); // 500ms delay so user sees success message
    } catch (error) {
      console.error("=== LOGIN ERROR DEBUG ===");
      console.error("Full error object:", error);
      console.error("error.response:", error.response);
      console.error("error.response?.data:", error.response?.data);
      console.error(
        "error.response?.data?.message:",
        error.response?.data?.message,
      );
      console.error(
        "error.response?.data?.data?.message:",
        error.response?.data?.data?.message,
      );
      console.error("========================");

      // Try multiple paths to get the message
      let errorMessage = "Invalid credentials. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.data?.message) {
        errorMessage = error.response.data.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.log("Final error message to show:", errorMessage);

      // Force a toast to show
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    console.log("=== Forgot Password Clicked ===");
    console.log("Current identifier:", identifier);
    console.log("Login type:", loginType);

    // Check if identifier is empty
    if (!identifier.trim()) {
      console.log("Identifier is empty, showing error");
      toast.error(
        `Please enter your ${loginType === "email" ? "email address" : "phone number"} first`,
      );
      return;
    }

    setForgotLoading(true);
    console.log(
      "Sending request to /auth/forgot-password with identifier:",
      identifier,
    );

    try {
      const response = await API.post("/auth/forgot-password", {
        identifier: identifier.trim(),
      });

      console.log("Response received:", response);

      if (response.data.status) {
        toast.success(
          response.data.message ||
            "A temporary password has been sent to your registered email. Please check your inbox.",
        );
        setPassword("");
      }
    } catch (error) {
      console.error("=== Forgot Password Error ===");
      console.error("Error object:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);

      const errorMessage =
        error.response?.data?.message ||
        "Failed to process request. Please try again.";
      toast.error(errorMessage);
    } finally {
      setForgotLoading(false);
    }
  };

  const toggleLoginType = () => {
    setLoginType(loginType === "email" ? "phone" : "email");
    setIdentifier("");
    setPassword("");
  };

  return (
    <div>
      <PublicNavbar />

      <div className="auth-container">
        <div className="auth-row">
          {/* Left Side - Animation & Content */}
          <div className="auth-left login-left">
            <div className="auth-animation">
              <Lottie
                animationData={loginAnimation}
                loop={true}
                style={{ width: "100%", height: "300px" }}
              />
            </div>

            <div className="auth-content">
              <h2>Welcome Back!</h2>
              <p>Login to continue your learning journey</p>

              <div className="auth-stats">
                <div className="stat">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Students Placed</span>
                </div>
                <div className="stat">
                  <span className="stat-number">95%</span>
                  <span className="stat-label">Success Rate</span>
                </div>
                <div className="stat">
                  <span className="stat-number">21+</span>
                  <span className="stat-label">Years Experience</span>
                </div>
              </div>

              <div className="auth-testimonial">
                <div className="stars">★★★★★</div>
                <p>"The best decision I made for my career!"</p>
                <span>- Rahul Sharma, Placed at TCS</span>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="auth-right">
            <div className="auth-card">
              <div className="auth-header">
                <h3>Login to Your Account</h3>
                <p>Enter your credentials to access your dashboard</p>
              </div>

              {/* Login Type Toggle */}
              <div className="login-type-toggle mb-3">
                <button
                  type="button"
                  className={`toggle-btn ${loginType === "email" ? "active" : ""}`}
                  onClick={toggleLoginType}
                >
                  <FaEnvelope className="me-2" /> Login with Email
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${loginType === "phone" ? "active" : ""}`}
                  onClick={toggleLoginType}
                >
                  <FaPhoneAlt className="me-2" /> Login with Phone
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    {loginType === "email" ? "Email Address" : "Phone Number"}
                  </label>
                  <div className="input-icon">
                    {loginType === "email" ? (
                      <FaEnvelope className="icon" />
                    ) : (
                      <FaPhoneAlt className="icon" />
                    )}
                    <input
                      type={loginType === "email" ? "email" : "number"}
                      className="form-control"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      required
                      placeholder={
                        loginType === "email"
                          ? "Enter your email"
                          : "Enter your phone number"
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="input-icon">
                    <FaLock className="icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>
                  <button
                    type="button"
                    className="forgot-link-btn"
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                  >
                    {forgotLoading ? "Sending..." : "Forgot Password?"}
                  </button>
                </div>

                <button type="submit" className="auth-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </button>
              </form>

              <div className="auth-footer">
                <p>
                  Don't have an account? <Link to="/register">Sign up now</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;
